<template>
  <div class="activity-stage">
    <div class="activity-container object-in-a-sentence">
      <div class="select-image">
        <div class="phrase">
          <span>{{ currentRound.text }}</span>
        </div>
        <div class="images">
          <div
            class="image-container"
            :key="answer.id"
            v-for="answer of currentRound.answers"
            @click="onSelectAnswer(answer)"
          >
            <img :src="answer.answerImage.image.src" :class="getImageClasses(answer.id)" />
          </div>
        </div>
      </div>
      <div class="typing" v-if="selectedAnswer" :transition="transitionsDisabled ? '' : 'fade-05'">
        <div class="round-text">
          <span>{{ currentRound.text }}&nbsp;</span>
        </div>
        <div class="color-block">
          <color-block-text :text="selectedAnswer.answer" :text-step="0"></color-block-text>
        </div>
        <span class="punctuation">{{ currentRound.punctuation }}</span>
      </div>
    </div>

    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import ColorBlockText from 'components/text-displays/ColorBlockText.vue';
import border from 'assets/svg/object-in-a-sentence/objectInASentenceBorder.svg';

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    ColorBlockText
  },

  data() {
    return {
      selectedAnswer: null,
      transitionsDisabled: false,
      border
    };
  },

  methods: {
    handleNewRound() {
      this.transitionsDisabled = true;
      this.selectedAnswer = null;

      this.$nextTick(this.fadeInActivity);
    },

    async loadActivity() {
      const activityState = this.getActivityState();
      await this.loadSounds(activityState);
      await this.loadImages(activityState);
      this.createRounds(activityState);
    },

    async loadImages(activityState) {
      const { rounds } = activityState;

      rounds.forEach(({ answers }) => {
        answers.forEach(({ answerImage }) => {
          this.queueImage(answerImage.image.src);
        });
      });
      return this.downloadImages();
    },

    async loadSounds(activityState) {
      const { rounds } = activityState;

      rounds.forEach(({ answers }) => {
        answers.forEach(({ answerAudio }) => {
          this.queueAudio(answerAudio.audio.src);
        });
      });
      await this.downloadAudio();
    },

    setContainerHeight() {
      const activity = document.querySelector('.object-in-a-sentence');
      const kbWrapper = document.querySelector('.phoenix-keyboard-wrapper');
      activity.style.height = `calc(100% - ${kbWrapper.offsetHeight}px)`;
    },

    onSelectAnswer(answer) {
      if (this.selectedAnswer?.id === answer.id) return;

      this.selectedAnswer = answer;

      const self = this;
      TweenMax.to('.select-image', 1, {
        top: '30%',
        onComplete() {
          self.enableKeyboard();
          self.overwriteRoundSteps({
            roundId: self.currentRound.id,
            steps: [{ answer: answer.answer }]
          });
          self.overwriteProgressText({ currentText: answer.answer });
        }
      });
    },

    getImageClasses(id) {
      const baseClasses = ['image', 'image-option'];
      if (!this.selectedAnswer) return baseClasses;

      return id === this.selectedAnswer.id
        ? [...baseClasses, 'image-selected']
        : [...baseClasses, 'image-disabled'];
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          delay: 6,
          written: 'Pick your favorite image. Then type the text.',
          voice: `${this.voiceInstructionsPrefix}/pickme-${this.getRandomVoice()}-1.mp3`
        }
      };
    },

    fadeInActivity() {
      this.setContainerHeight();
      this.$nextTick(() => {
        TweenMax.to('.object-in-a-sentence', 0.5, { opacity: 1 });
        this.transitionsDisabled = false;
      });
    },

    startActivity() {
      this.showKeyboard({ enable: false });
      this.setupNewRound();
      this.errorLoggerStart();
      TweenMax.delayedCall(0.5, this.fadeInActivity);
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();

        if (this.selectedAnswer) {
          this.cache.get(this.selectedAnswer.answerAudio.audio.src).play();
        }

        if (this.isEndOfActivity) {
          TweenMax.delayedCall(2, this.completeActivity);
          return;
        }

        TweenMax.delayedCall(1, () => {
          TweenMax.to('.object-in-a-sentence', 0.5, {
            opacity: 0,
            onComplete() {
              TweenMax.to('.select-image', 0, { top: '50%' });
            }
          });
        });

        TweenMax.delayedCall(2, () => {
          this.handleNewRound();
          this.setupNewRound();
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

.select-image {
  display: flex;
  flex-direction: row;
  justify-content: center;
  position: absolute;
  height: auto;
  transform: translateY(-50%);
  width: 100%;
  top: 50%;
  left: 0;

  .phrase {
    padding-left: 20px;
    padding-right: 5px;
  }
  .images {
    padding-left: 5px;
    padding-right: 20px;
  }

  .phrase,
  .images {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.punctuation,
.round-text,
.color-block,
.phrase {
  font-weight: normal;
  display: inline-block;
  font-family: 'hwtprint';
  font-size: 26px;
}

.typing {
  top: 65%;
  width: 100%;
  position: absolute;
  transform: translateY(-50%);
  text-align: center;
}

.image-container {
  position: relative;
  margin: 0 5px;
  .image {
    transition: 0.5s all;
    border: 2px dashed #aaaaaa;
    border-radius: 8px;
  }
  .image-selected {
    border: 2px solid #000000;
    border-radius: 8px;
  }
  .image-option {
    width: 160px;
    max-width: 160px;
    height: auto;
    cursor: pointer;
  }
  .image-disabled {
    opacity: 0.4;
  }
}

.object-in-a-sentence {
  opacity: 0;
  position: relative;
}
</style>
